import React, { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "react-oidc-context";
import {
  FaDownload,
  FaFileAlt,
  FaSpinner,
  FaUserTie,
  FaBuilding,
} from "react-icons/fa";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";

// API Config
const TRANS_API = `https://${process.env.REACT_APP_API_KEY_TRANS}.execute-api.ap-south-1.amazonaws.com/default/branchTransaction`;
const SUMMART_API = `https://${process.env.REACT_APP_API_KEY_SUMMARY}.execute-api.ap-south-1.amazonaws.com/default/getAllBranchSummary`;
const BRANCH_API = `https://${process.env.REACT_APP_API_KEY_AUTH_BRANCH}.execute-api.ap-south-1.amazonaws.com/default/createBranch`;
const BANKER_TRANS_API = `https://${process.env.REACT_APP_API_KEY_AUTH_BANKER_TRANS}.execute-api.ap-south-1.amazonaws.com/default/bankersTransaction`;
const BANKER_LIST_URL = `https://${process.env.REACT_APP_API_KEY_AUTH_BANKER}.execute-api.ap-south-1.amazonaws.com/default/createBankers`;

const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;
const toISTDate = (ts) => ts
  ? new Date(new Date(ts).getTime() + IST_OFFSET_MS).toISOString().split("T")[0]
  : null;

const AdminStatement = () => {
  const auth = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [branches, setBranches] = useState([]);
  const [bankers, setBankers] = useState([]);
  const [loading, setLoading] = useState(true);
  // Cache the last fetched branch summary so we don't re-fetch for each branch in "download all"
  const branchSummaryRef = useRef(null);
  const summaryDateRef = useRef({ start: null, end: null });

  // Filter States
  const istToday = new Date(Date.now() + IST_OFFSET_MS).toISOString().split("T")[0];
  const [startDate, setStartDate] = useState(istToday);
  const [endDate, setEndDate] = useState(istToday);
  const [viewMode, setViewMode] = useState("BRANCH");
  const [selection, setSelection] = useState("ALL");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [tRes, bRes, btRes, blRes] = await Promise.all([
        fetch(TRANS_API),
        fetch(BRANCH_API),
        fetch(BANKER_TRANS_API),
        fetch(BANKER_LIST_URL),
      ]);

      const tData = await tRes.json();
      const bData = await bRes.json();
      const btData = await btRes.json();
      const blData = await blRes.json();

      setTransactions([
        ...(tData.transactions || []),
        ...(btData.transactions || []),
      ]);
      setBranches(bData.branches || []);

      // Update: Data is in blData.branches, names are in bankersName per console log
      setBankers(blData.branches || blData.bankers || []);
    } catch (err) {
      console.error("Fetch Error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch branch summary from backend (same source as AdminHome) for accurate opBal/closing
  const fetchBranchSummary = useCallback(async (s, e) => {
    // Return cached if dates haven't changed
    if (
      branchSummaryRef.current &&
      summaryDateRef.current.start === s &&
      summaryDateRef.current.end === e
    ) {
      return branchSummaryRef.current;
    }
    const token = auth.user?.access_token;
    if (!token) return null;
    try {
      const res = await fetch(`${SUMMART_API}?start=${s}&end=${e}`, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      if (!res.ok) return null;
      const data = await res.json();
      branchSummaryRef.current = data.summary || {};
      summaryDateRef.current = { start: s, end: e };
      return branchSummaryRef.current;
    } catch {
      return null;
    }
  }, [auth.user?.access_token]);

  // --- BRANCH LEDGER DOWNLOAD ---
  const handleDownloadBranch = async () => {
    const branchId = selection;

    // 1. DATA SEGREGATION & OP-BAL LOGIC
    const data = {
      inflow: [],
      outflow: [],
      btIn: [],
      btOut: [],
      expenses: [],
      totals: { in: 0, out: 0, btIn: 0, btOut: 0, exp: 0, comm: 0 },
      opBal: 0, // Initialized to 0
    };

    // Build row lists from raw transactions (for table display only)
    transactions.forEach((tx) => {
      const isMyRole =
        (tx.fromBranch === branchId && tx.flowRole === "SENDER") ||
        (tx.toBranch === branchId && tx.flowRole === "RECEIVER") ||
        tx.thirdParty === branchId;
      if (!isMyRole || ["REJECTED", "SPLIT"].includes(tx.status)) return;
      if (tx.status === "PENDING" && tx.flowRole === "RECEIVER") return;
      const txDate = toISTDate(tx.timestamp);
      if (!txDate || txDate < startDate || txDate > endDate) return;
      const isThisLedgerThirdParty = tx.thirdParty === branchId;
      if (["BTIN", "BTSPLITIN"].includes(tx.paymentType)) data.btIn.push(tx);
      else if (["BTOUT", "BTSPLITOUT"].includes(tx.paymentType)) data.btOut.push(tx);
      else if (tx.paymentType === "EXP") data.expenses.push(tx);
      else if (["IN", "SPLITIN"].includes(tx.paymentType) && !isThisLedgerThirdParty) data.inflow.push(tx);
      else if (["OUT", "SPLITOUT"].includes(tx.paymentType) && !isThisLedgerThirdParty) data.outflow.push(tx);
    });

    // 2. Use backend summary for all numeric totals (same source as AdminHome View Details)
    const summary = await fetchBranchSummary(startDate, endDate);
    const bs = summary?.[branchId] || {};
    data.opBal        = bs.opBal    ?? 0;
    data.totals.in    = bs.totalIn  ?? 0;
    data.totals.btIn  = bs.btIn     ?? 0;
    data.totals.out   = bs.totalOut ?? 0;
    data.totals.btOut = bs.btOut    ?? 0;
    data.totals.comm  = bs.comm     ?? 0;
    data.totals.exp   = bs.exp      ?? 0;

    if (
      data.inflow.length === 0 &&
      data.outflow.length === 0 &&
      selection !== "ALL" &&
      data.opBal === 0
    ) {
      alert("No data found for this range.");
      return;
    }

    // 3. FINAL CALCULATIONS
    const calculatedClosing = bs.closingBal ??
      (data.opBal + data.totals.in + data.totals.btIn + data.totals.comm
        - data.totals.out - data.totals.btOut - data.totals.exp);

    // Helper functions for names
    const getPartnerIn = (item) =>
      !item ? "" : item.toBranch === branchId ? item.fromBranch : item.toBranch;
    const getPartnerOut = (item) =>
      !item
        ? ""
        : item.fromBranch === branchId
          ? item.toBranch
          : item.fromBranch;

    // 3. ROW CONSTRUCTION
    const rowData = [
      [
        `Branch Ledger: ${branchId}`,
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "SUMMARY",
        "VALUE",
        "",
        "BT IN DETAILS",
        "",
      ],
      [
        "S.NO",
        "NAME (IN)",
        "IN",
        "COM",
        "DATE",
        "REMARKS",
        "NAME (OUT)",
        "OUT",
        "COM",
        "DATE",
        "REMARKS",
        "",
        "",
        "",
        "",
      ],
    ];

    const maxRows = Math.max(
      data.inflow.length,
      data.outflow.length,
      12,
      data.btIn.length + data.btOut.length + 5,
    );

    for (let i = 0; i < maxRows; i++) {
      const inItem = data.inflow[i];
      const outItem = data.outflow[i];
      const formatDate = (ts) => toISTDate(ts) || "";
      const row = [
        i + 1,
        getPartnerIn(inItem),
        inItem?.amount || "",
        inItem?.commission || "",
        formatDate(inItem?.timestamp),
        inItem?.remarks || "",
        getPartnerOut(outItem),
        outItem?.amount || "",
        outItem?.commission || "",
        formatDate(outItem?.timestamp),
        outItem?.remarks || "",
        "",
      ];

      // MIDDLE BOX: Use the calculated opBal
      if (i === 0) row.push("OPENING", data.opBal);
      else if (i === 1) row.push("TOTAL IN", data.totals.in);
      else if (i === 2) row.push("BT IN (+)", data.totals.btIn);
      else if (i === 3) row.push("TOTAL OUT", data.totals.out);
      else if (i === 4) row.push("BT OUT (-)", data.totals.btOut);
      else if (i === 5) row.push("COMMISSION", data.totals.comm);
      else if (i === 6) row.push("EXPENSES", "---");
      else if (i < 7 + data.expenses.length) {
        const ex = data.expenses[i - 7];
        row.push(ex.remarks || "Exp", ex.amount);
      } else if (i === 7 + data.expenses.length)
        row.push("EXP TOTAL", data.totals.exp);
      else if (i === 8 + data.expenses.length)
        row.push("CLOSING BAL.", calculatedClosing);
      else row.push("", "");

      row.push(""); // Spacer

      // RIGHT SIDE: BT DETAILS
      if (i === 0) row.push("BT IN DETAILS", "");
      else if (i > 0 && i <= data.btIn.length) {
        const tx = data.btIn[i - 1];
        row.push(
          tx.fromBranch === branchId ? tx.toBranch : tx.fromBranch,
          tx.amount,
        );
      } else if (i === data.btIn.length + 1 && data.btIn.length > 0) {
        row.push("TOTAL BT IN", data.totals.btIn);
      } else if (i === data.btIn.length + 3) {
        row.push("BT OUT DETAILS", "");
      } else if (i > data.btIn.length + 3) {
        const btOutIdx = i - (data.btIn.length + 4);
        const tx = data.btOut[btOutIdx];
        if (tx)
          row.push(
            tx.fromBranch === branchId ? tx.toBranch : tx.fromBranch,
            tx.amount,
          );
        else if (btOutIdx === data.btOut.length && data.btOut.length > 0)
          row.push("TOTAL BT OUT", data.totals.btOut);
        else row.push("", "");
      } else {
        row.push("", "");
      }

      rowData.push(row);
    }

    // 4. GENERATE EXCEL
    const ws = XLSX.utils.aoa_to_sheet(rowData);
    const wb = XLSX.utils.book_new();
    ws["!cols"] = [
      { wch: 5 },
      { wch: 15 },
      { wch: 12 },
      { wch: 8 },
      { wch: 5 },
      { wch: 15 },
      { wch: 12 },
      { wch: 8 },
      { wch: 5 },
      { wch: 2 },
      { wch: 20 },
      { wch: 15 },
      { wch: 2 },
      { wch: 20 },
      { wch: 15 },
    ];
    XLSX.utils.book_append_sheet(wb, ws, "Ledger");
    XLSX.writeFile(wb, `${branchId}_Ledger_${startDate}_to_${endDate}.xlsx`);
  };

  // --- BANKER LEDGER DOWNLOAD ---
  const handleDownloadBanker = () => {
    const bankerId = selection;
    const rangeData = transactions.filter((tx) => {
      const txDate = toISTDate(tx.timestamp);
      return (
        txDate >= startDate &&
        txDate <= endDate &&
        (selection === "ALL" ||
          tx.fromBranch === bankerId ||
          tx.toBranch === bankerId ||
          tx.name === bankerId ||
          tx.bankerName === bankerId ||
          tx.bankersName === bankerId) &&
        tx.status !== "REJECTED"
      );
    });

    if (rangeData.length === 0) return alert("No data found.");

    const ins = rangeData.filter((tx) =>
      ["IN", "BTIN", "SPLITIN"].includes(tx.paymentType),
    );
    const outs = rangeData.filter((tx) =>
      ["OUT", "BTOUT", "SPLITOUT"].includes(tx.paymentType),
    );
    const exps = rangeData.filter((tx) => tx.paymentType === "EXP");

    const tIn = ins.reduce((s, t) => s + Number(t.amount || 0), 0);
    const tOut = outs.reduce((s, t) => s + Number(t.amount || 0), 0);
    const tExp = exps.reduce((s, t) => s + Number(t.amount || 0), 0);
    const tComm = rangeData.reduce((s, t) => s + Number(t.commission || 0), 0);
    const closing = 0 + tIn + tComm - (tOut + tExp);

    const rowData = [
      [
        "S.NO",
        "DATE",
        "NAME (IN)",
        "IN",
        "COMM",
        "DETAILS",
        "NAME (OUT)",
        "OUT",
        "COMM",
        "DETAILS",
        "",
        "SUMMARY",
        "VALUE",
      ],
    ];
    const maxR = Math.max(ins.length, outs.length, 12, exps.length + 10);

    for (let i = 0; i < maxR; i++) {
      const inT = ins[i],
        outT = outs[i];
      const row = [
        i + 1,
        toISTDate(inT?.timestamp) || toISTDate(outT?.timestamp) || "",
        inT?.name || "",
        inT?.amount || "",
        inT?.commission || "",
        inT?.remarks || "",
        outT?.name || "",
        outT?.amount || "",
        outT?.commission || "",
        outT?.remarks || "",
        "",
      ];

      if (i === 0) row.push("OPENING", 0);
      else if (i === 2) row.push("TOTAL IN", tIn);
      else if (i === 3) row.push("TOTAL COMM (+)", tComm);
      else if (i === 5) row.push("TOTAL OUT", tOut);
      else if (i === 7) row.push("EXPENSES DETAILS", "");
      else if (i > 7 && i <= 7 + exps.length)
        row.push(
          exps[i - 8].name || exps[i - 8].remarks || "Exp",
          exps[i - 8].amount,
        );
      else if (i === 8 + exps.length) row.push("EXP TOTAL", tExp);
      else if (i === 9 + exps.length) row.push("CLOSING BAL.", closing);
      else row.push("", "");

      rowData.push(row);
    }
    const ws = XLSX.utils.aoa_to_sheet(rowData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "BankerLedger");
    XLSX.writeFile(
      wb,
      `${bankerId}_Banker_Ledger_${startDate}_to_${endDate}.xlsx`,
    );
  };
  // Add this helper function inside the AdminStatement component
  // to generate the data for a specific branch sheet.
  // branchSummaryMap: { [branchId]: { opBal, totalIn, btIn, totalOut, btOut, comm, exp, closingBal } }
  const generateBranchSheetData = (branchId, transactions, startDate, endDate, branchSummaryMap) => {
    const data = {
      inflow: [],
      outflow: [],
      btIn: [],
      btOut: [],
      expenses: [],
      totals: { in: 0, out: 0, btIn: 0, btOut: 0, exp: 0, comm: 0 },
      opBal: 0,
    };

    // Build row lists from raw transactions (table display only — no numeric totals here)
    transactions.forEach((tx) => {
      const isMyRole =
        (tx.fromBranch === branchId && tx.flowRole === "SENDER") ||
        (tx.toBranch === branchId && tx.flowRole === "RECEIVER") ||
        tx.thirdParty === branchId;
      if (!isMyRole || ["REJECTED", "SPLIT"].includes(tx.status)) return;
      if (tx.status === "PENDING" && tx.flowRole === "RECEIVER") return;
      const txDate = toISTDate(tx.timestamp);
      if (!txDate || txDate < startDate || txDate > endDate) return;
      const isThisLedgerThirdParty = tx.thirdParty === branchId;
      if (["BTIN", "BTSPLITIN"].includes(tx.paymentType)) data.btIn.push(tx);
      else if (["BTOUT", "BTSPLITOUT"].includes(tx.paymentType)) data.btOut.push(tx);
      else if (tx.paymentType === "EXP") data.expenses.push(tx);
      else if (["IN", "SPLITIN"].includes(tx.paymentType) && !isThisLedgerThirdParty) data.inflow.push(tx);
      else if (["OUT", "SPLITOUT"].includes(tx.paymentType) && !isThisLedgerThirdParty) data.outflow.push(tx);
    });

    // Use backend summary for accurate totals (same source as AdminHome)
    const bs = branchSummaryMap?.[branchId] || {};
    data.opBal        = bs.opBal    ?? 0;
    data.totals.in    = bs.totalIn  ?? 0;
    data.totals.btIn  = bs.btIn     ?? 0;
    data.totals.out   = bs.totalOut ?? 0;
    data.totals.btOut = bs.btOut    ?? 0;
    data.totals.comm  = bs.comm     ?? 0;
    data.totals.exp   = bs.exp      ?? 0;

    const calculatedClosing = bs.closingBal ??
      (data.opBal + data.totals.in + data.totals.btIn + data.totals.comm
        - data.totals.out - data.totals.btOut - data.totals.exp);

    const getPartnerIn = (item) =>
      !item ? "" : item.toBranch === branchId ? item.fromBranch : item.toBranch;
    const getPartnerOut = (item) =>
      !item
        ? ""
        : item.fromBranch === branchId
          ? item.toBranch
          : item.fromBranch;

    const rowData = [
      [
        `Branch Ledger: ${branchId}`,
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "SUMMARY",
        "VALUE",
        "",
        "BT IN DETAILS",
        "",
      ],
      [
        "S.NO",
        "NAME (IN)",
        "IN",
        "COM",
        "DATE",
        "NAME (OUT)",
        "OUT",
        "COM",
        "DATE",
        "",
        "",
        "",
        "",
        "",
        "",
      ],
    ];

    const maxRows = Math.max(
      data.inflow.length,
      data.outflow.length,
      12,
      data.btIn.length + data.btOut.length + 5,
    );

    for (let i = 0; i < maxRows; i++) {
      const inItem = data.inflow[i];
      const outItem = data.outflow[i];
      const row = [
        i + 1,
        getPartnerIn(inItem),
        inItem?.amount || "",
        inItem?.commission || "",
        "",
        getPartnerOut(outItem),
        outItem?.amount || "",
        outItem?.commission || "",
        "",
        "",
      ];

      if (i === 0) row.push("OPENING", data.opBal);
      else if (i === 1) row.push("TOTAL IN", data.totals.in);
      else if (i === 2) row.push("BT IN (+)", data.totals.btIn);
      else if (i === 3) row.push("TOTAL OUT", data.totals.out);
      else if (i === 4) row.push("BT OUT (-)", data.totals.btOut);
      else if (i === 5) row.push("COMMISSION", data.totals.comm);
      else if (i === 6) row.push("EXPENSES", "---");
      else if (i < 7 + data.expenses.length) {
        const ex = data.expenses[i - 7];
        row.push(ex.remarks || "Exp", ex.amount);
      } else if (i === 7 + data.expenses.length)
        row.push("EXP TOTAL", data.totals.exp);
      else if (i === 8 + data.expenses.length)
        row.push("CLOSING BAL.", calculatedClosing);
      else row.push("", "");

      row.push("");

      if (i === 0) row.push("BT IN DETAILS", "");
      else if (i > 0 && i <= data.btIn.length) {
        const tx = data.btIn[i - 1];
        row.push(`${tx.fromBranch} to ${tx.toBranch}`, tx.amount);
      } else if (i === data.btIn.length + 1 && data.btIn.length > 0) {
        row.push("TOTAL BT IN", data.totals.btIn);
      } else if (i === data.btIn.length + 3) {
        row.push("BT OUT DETAILS", "");
      } else if (i > data.btIn.length + 3) {
        const btOutIdx = i - (data.btIn.length + 4);
        const tx = data.btOut[btOutIdx];
        if (tx) row.push(`${tx.fromBranch} to ${tx.toBranch}`, tx.amount);
        else if (btOutIdx === data.btOut.length && data.btOut.length > 0)
          row.push("TOTAL BT OUT", data.totals.btOut);
        else row.push("", "");
      } else {
        row.push("", "");
      }
      rowData.push(row);
    }
    return rowData;
  };

  // --- NEW FUNCTION TO DOWNLOAD ALL ---
  const handleDownloadAllBranches = async () => {
    if (branches.length === 0) return alert("No branches found.");

    setLoading(true);
    const summaryMap = await fetchBranchSummary(startDate, endDate) || {};
    const wb = XLSX.utils.book_new();

    branches.forEach((b) => {
      const branchName = b.branchName;
      const sheetData = generateBranchSheetData(
        branchName,
        transactions,
        startDate,
        endDate,
        summaryMap,
      );
      const ws = XLSX.utils.aoa_to_sheet(sheetData);

      // Set column widths
      ws["!cols"] = [
        { wch: 5 },
        { wch: 15 },
        { wch: 12 },
        { wch: 8 },
        { wch: 5 },
        { wch: 15 },
        { wch: 12 },
        { wch: 8 },
        { wch: 5 },
        { wch: 2 },
        { wch: 20 },
        { wch: 15 },
        { wch: 2 },
        { wch: 20 },
        { wch: 15 },
      ];

      // Excel sheet names cannot exceed 31 chars
      const safeName = branchName.substring(0, 30);
      XLSX.utils.book_append_sheet(wb, ws, safeName);
    });

    XLSX.writeFile(wb, `Master_Ledger_${startDate}_to_${endDate}.xlsx`);
    setLoading(false);
  };

  // --- MASTER FUNCTION FOR ALL BANKERS ---
  const handleDownloadAllBankers = () => {
    if (bankers.length === 0) return alert("No bankers found.");

    setLoading(true);
    try {
      // 1. Initialize the Master Workbook
      const wb = XLSX.utils.book_new();
      let hasFoundAnyData = false;

      // 2. Loop through every banker in your state
      bankers.forEach((bk) => {
        // Use the name key found in your bankers API (bankersName)
        const bankerId = bk.bankersName || bk.bankerName;
        if (!bankerId) return;

        // 3. Filter transactions for THIS specific banker in the loop
        const rangeData = transactions.filter((tx) => {
          const txDate = toISTDate(tx.timestamp);
          const isThisBanker =
            tx.fromBranch === bankerId ||
            tx.toBranch === bankerId ||
            tx.name === bankerId ||
            tx.bankerName === bankerId ||
            tx.bankersName === bankerId;

          return (
            txDate >= startDate &&
            txDate <= endDate &&
            isThisBanker &&
            tx.status !== "REJECTED"
          );
        });

        // Skip this sheet if the banker has no transactions in this date range
        if (rangeData.length === 0) return;
        hasFoundAnyData = true;

        // 4. Category Splitting
        const ins = rangeData.filter((tx) =>
          ["IN", "BTIN", "SPLITIN"].includes(tx.paymentType),
        );
        const outs = rangeData.filter((tx) =>
          ["OUT", "BTOUT", "SPLITOUT"].includes(tx.paymentType),
        );
        const exps = rangeData.filter((tx) => tx.paymentType === "EXP");

        // 5. Math Calculations
        const tIn = ins.reduce((s, t) => s + Number(t.amount || 0), 0);
        const tOut = outs.reduce((s, t) => s + Number(t.amount || 0), 0);
        const tExp = exps.reduce((s, t) => s + Number(t.amount || 0), 0);
        const tComm = rangeData.reduce(
          (s, t) => s + Number(t.commission || 0),
          0,
        );
        const closing = 0 + tIn + tComm - (tOut + tExp);

        // 6. Row Construction (Matching your banker layout)
        const rowData = [
          [
            `Banker Ledger: ${bankerId}`,
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "SUMMARY",
            "VALUE",
          ],
          [
            "S.NO",
            "DATE",
            "NAME (IN)",
            "IN",
            "COMM",
            "DETAILS",
            "NAME (OUT)",
            "OUT",
            "COMM",
            "DETAILS",
            "",
            "",
            "",
          ],
        ];

        const maxR = Math.max(ins.length, outs.length, 12, exps.length + 10);

        for (let i = 0; i < maxR; i++) {
          const inT = ins[i],
            outT = outs[i];
          const row = [
            i + 1,
            toISTDate(inT?.timestamp) || toISTDate(outT?.timestamp) || "",
            inT?.name || "",
            inT?.amount || "",
            inT?.commission || "",
            inT?.remarks || "",
            outT?.name || "",
            outT?.amount || "",
            outT?.commission || "",
            outT?.remarks || "",
            "",
          ];

          if (i === 0) row.push("OPENING", 0);
          else if (i === 2) row.push("TOTAL IN", tIn);
          else if (i === 3) row.push("TOTAL COMM (+)", tComm);
          else if (i === 5) row.push("TOTAL OUT", tOut);
          else if (i === 7) row.push("EXPENSES DETAILS", "");
          else if (i > 7 && i <= 7 + exps.length) {
            const ex = exps[i - 8];
            row.push(ex.name || ex.remarks || "Exp", ex.amount);
          } else if (i === 8 + exps.length) row.push("EXP TOTAL", tExp);
          else if (i === 9 + exps.length) row.push("CLOSING BAL.", closing);
          else row.push("", "");

          rowData.push(row);
        }

        // 7. Create Worksheet and Format
        const ws = XLSX.utils.aoa_to_sheet(rowData);
        ws["!cols"] = [
          { wch: 5 },
          { wch: 12 },
          { wch: 15 },
          { wch: 12 },
          { wch: 10 },
          { wch: 20 },
          { wch: 15 },
          { wch: 12 },
          { wch: 10 },
          { wch: 20 },
          { wch: 5 },
          { wch: 20 },
          { wch: 15 },
        ];

        // Excel Sheet name limit (31 chars)
        const safeSheetName = bankerId.toString().substring(0, 31);
        XLSX.utils.book_append_sheet(wb, ws, safeSheetName);
      });

      // 8. Final Export
      if (!hasFoundAnyData) {
        alert("No data found for any banker in this range.");
      } else {
        XLSX.writeFile(
          wb,
          `All_Bankers_Report_${startDate}_to_${endDate}.xlsx`,
        );
      }
    } catch (err) {
      console.error("Master Export Error:", err);
    } finally {
      setLoading(false);
    }
  };
  if (loading)
    return (
      <div style={{ textAlign: "center", padding: "100px" }}>
        <FaSpinner className="spin" size={30} />
        <p>Loading Data...</p>
      </div>
    );

  return (
    <div
      className="admin-container"
      style={{ padding: "20px", maxWidth: "800px", margin: "0 auto" }}
    >
      <header
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: "30px",
          background: "#1e293b",
          color: "#fff",
          padding: "15px",
          borderRadius: "8px",
        }}
      >
        <h1 style={{ fontSize: "1.2rem" }}>
          <FaFileAlt /> Admin Ledger Statements
        </h1>
        <Link to="/">
          <button
            style={{
              background: "#3b82f6",
              color: "#fff",
              border: "none",
              padding: "8px 15px",
              borderRadius: "5px",
              cursor: "pointer",
            }}
          >
            Back to Dashboard
          </button>
        </Link>
      </header>

      <div
        style={{
          background: "#fff",
          padding: "30px",
          borderRadius: "12px",
          boxShadow: "0 10px 25px rgba(0,0,0,0.05)",
        }}
      >
        <div style={{ display: "flex", gap: "20px", marginBottom: "25px" }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              style={inputStyle}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              style={inputStyle}
            />
          </div>
        </div>

        <div style={{ marginBottom: "25px" }}>
          <label style={labelStyle}>Statement Category</label>
          <div style={{ display: "flex", gap: "10px", marginTop: "10px" }}>
            <button
              onClick={() => {
                setViewMode("BRANCH");
                setSelection("ALL");
              }}
              style={viewMode === "BRANCH" ? activeTab : inactiveTab}
            >
              <FaBuilding /> Branch Wise
            </button>
            <button
              onClick={() => {
                setViewMode("BANKER");
                setSelection("ALL");
              }}
              style={viewMode === "BANKER" ? activeTab : inactiveTab}
            >
              <FaUserTie /> Banker Wise
            </button>
          </div>
        </div>

        <div style={{ marginBottom: "35px" }}>
          <label style={labelStyle}>
            Select {viewMode === "BRANCH" ? "Branch" : "Banker"}
          </label>
          <select
            value={selection}
            onChange={(e) => setSelection(e.target.value)}
            style={inputStyle}
          >
            <option value="ALL">
              Download All {viewMode === "BRANCH" ? "Branches" : "Bankers"}{" "}
              Combined File
            </option>
            {viewMode === "BRANCH"
              ? branches.map((b) => (
                  <option key={b.id || b.email} value={b.branchName}>
                    {b.branchName}
                  </option>
                ))
              : bankers.map((bk) => (
                  <option key={bk.id || bk.email} value={bk.bankersName}>
                    {bk.bankersName}
                  </option>
                ))}
          </select>
        </div>

        <button
          onClick={() => {
            if (viewMode === "BRANCH") {
              selection === "ALL"
                ? handleDownloadAllBranches()
                : handleDownloadBranch();
            } else {
              selection === "ALL"
                ? handleDownloadAllBankers()
                : handleDownloadBanker();
            }
          }}
          style={downloadBtnStyle}
          disabled={loading}
        >
          <FaDownload />{" "}
          {loading
            ? "Generating..."
            : selection === "ALL"
              ? "Download All Bankers"
              : "Generate Excel Report"}
        </button>
      </div>
    </div>
  );
};

const labelStyle = {
  display: "block",
  marginBottom: "8px",
  fontWeight: "bold",
  color: "#475569",
  fontSize: "0.9rem",
};
const inputStyle = {
  width: "100%",
  padding: "12px",
  borderRadius: "8px",
  border: "1px solid #e2e8f0",
  outline: "none",
};
const activeTab = {
  flex: 1,
  padding: "12px",
  background: "#0f172a",
  color: "#fff",
  border: "none",
  borderRadius: "8px",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
};
const inactiveTab = {
  flex: 1,
  padding: "12px",
  background: "#f8fafc",
  color: "#64748b",
  border: "1px solid #e2e8f0",
  borderRadius: "8px",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
};
const downloadBtnStyle = {
  width: "100%",
  padding: "16px",
  background: "#16a34a",
  color: "#fff",
  border: "none",
  borderRadius: "8px",
  cursor: "pointer",
  fontWeight: "bold",
  fontSize: "1rem",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "10px",
};

export default AdminStatement;
